// src/pages/Driverspage/DriverDetails.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { driversService, jobsService } from "../../services";
import "./DriverDetails.css";

// Job status options for filtering
const JOB_STATUS_OPTIONS = [
  { value: '', label: 'All Jobs' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Assigned', label: 'Assigned' },
  { value: 'In Transit', label: 'In Transit' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Exception', label: 'Exception' },
  { value: 'Cancelled', label: 'Cancelled' },
];

// Status badge helper
const getStatusClass = (status) => {
  const statusMap = {
    'Online': 'driver-status-online',
    'Offline': 'driver-status-offline',
    'Delayed': 'driver-status-delayed',
  };
  return statusMap[status] || 'driver-status-offline';
};

// Job status badge helper
const getJobStatusClass = (status) => {
  const statusMap = {
    'Pending': 'job-status-pending',
    'Assigned': 'job-status-assigned',
    'In Transit': 'job-status-transit',
    'Delivered': 'job-status-delivered',
    'Exception': 'job-status-exception',
    'Cancelled': 'job-status-cancelled',
  };
  return statusMap[status] || '';
};

export default function DriverDetails() {
  const { id: driverId } = useParams();
  const navigate = useNavigate();

  // Driver state
  const [driver, setDriver] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsPagination, setJobsPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [jobStatusFilter, setJobStatusFilter] = useState('');
  const [jobSearch, setJobSearch] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Location modal state
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Fetch driver details and performance
  const fetchDriverDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [driverData, performanceData] = await Promise.all([
        driversService.getDriverById(driverId),
        driversService.getDriverPerformance(driverId),
      ]);

      setDriver(driverData);
      setPerformance(performanceData);
    } catch (err) {
      console.error('Fetch driver error:', err);
      setError('Failed to load driver details');
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  // Fetch driver's jobs
  const fetchDriverJobs = useCallback(async (page = 1) => {
    try {
      setJobsLoading(true);

      const filters = {
        page,
        limit: 10,
        driver: driverId,
        status: jobStatusFilter || undefined,
        search: jobSearch || undefined,
        sortBy: 'createdAt',
        order: 'desc',
      };

      const response = await jobsService.getJobs(filters);

      setJobs(response.jobs || []);
      setJobsPagination({
        page: response.pagination?.page || page,
        totalPages: response.pagination?.totalPages || 1,
        total: response.pagination?.total || 0,
      });
    } catch (err) {
      console.error('Fetch jobs error:', err);
    } finally {
      setJobsLoading(false);
    }
  }, [driverId, jobStatusFilter, jobSearch]);

  // Initial fetch
  useEffect(() => {
    fetchDriverDetails();
  }, [fetchDriverDetails]);

  // Fetch jobs when filters change
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchDriverJobs(1);
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchDriverJobs]);

  // Handle status update
  const handleUpdateStatus = async (newStatus) => {
    try {
      await driversService.updateStatus(driverId, newStatus);
      fetchDriverDetails();
    } catch (err) {
      console.error('Update status error:', err);
      alert('Failed to update status');
    }
  };

  // Open edit modal
  const openEditModal = () => {
    if (!driver) return;
    setEditForm({
      firstName: driver.firstName || '',
      lastName: driver.lastName || '',
      email: driver.email || '',
      phone: driver.phone || '',
      vehicle: driver.driverProfile?.vehicle || '',
      licenseNumber: driver.driverProfile?.licenseNumber || '',
    });
    setShowEditModal(true);
  };

  // Save edit
  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      await driversService.updateDriver(driverId, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone,
        driverProfile: {
          vehicle: editForm.vehicle,
          licenseNumber: editForm.licenseNumber,
        },
      });
      setShowEditModal(false);
      fetchDriverDetails();
    } catch (err) {
      console.error('Update driver error:', err);
      alert('Failed to update driver');
    } finally {
      setSaving(false);
    }
  };

  // Handle deactivate
  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate this driver?')) return;

    try {
      await driversService.deleteDriver(driverId);
      navigate('/drivers');
    } catch (err) {
      console.error('Deactivate error:', err);
      alert('Failed to deactivate driver');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle jobs page change
  const handleJobsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= jobsPagination.totalPages) {
      fetchDriverJobs(newPage);
    }
  };

  if (loading) {
    return (
      <div className="driver-details-container">
        <div className="driver-details-loading">
          <div className="driver-details-spinner"></div>
          <p>Loading driver details...</p>
        </div>
      </div>
    );
  }

  if (error || !driver) {
    return (
      <div className="driver-details-container">
        <div className="driver-details-error">
          <p>{error || 'Driver not found'}</p>
          <button onClick={() => navigate('/drivers')}>Back to Drivers</button>
        </div>
      </div>
    );
  }

  return (
    <div className="driver-details-container">
      {/* Header */}
      <div className="driver-details-header">
        <button className="mq-back-btn" onClick={() => navigate('/drivers')}>
          <span className="mq-back-btn-icon">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Back to Drivers
        </button>
      </div>

      {/* Profile Card */}
      <div className="driver-profile-card">
        <div className="driver-profile-main">
          <div className="driver-profile-avatar">
            {driver.avatar ? (
              <img src={driver.avatar} alt={driver.firstName} />
            ) : (
              driver.firstName?.[0] || 'D'
            )}
          </div>
          <div className="driver-profile-info">
            <h1 className="driver-profile-name">{driver.firstName} {driver.lastName}</h1>
            <p className="driver-profile-email">{driver.email}</p>
            <div className="driver-profile-badges">
              <span className={`driver-status-badge ${getStatusClass(driver.driverProfile?.status)}`}>
                {driver.driverProfile?.status || 'Offline'}
              </span>
              {driver.rating > 0 && (
                <span className="driver-rating-badge">
                  ★ {driver.rating?.toFixed(1)} ({driver.totalReviews || 0} reviews)
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="driver-profile-actions">
          <button className="driver-action-btn primary" onClick={openEditModal}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M11.333 2a1.886 1.886 0 012.667 2.667l-9.166 9.166L2 14.5l.667-2.833L11.333 2z" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            Edit Driver
          </button>
          <div className="driver-status-dropdown">
            <button className="driver-action-btn secondary">
              Set Status
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="driver-status-menu">
              {driver.driverProfile?.status !== 'Online' && (
                <button onClick={() => handleUpdateStatus('Online')}>Set Online</button>
              )}
              {driver.driverProfile?.status !== 'Offline' && (
                <button onClick={() => handleUpdateStatus('Offline')}>Set Offline</button>
              )}
              {driver.driverProfile?.status !== 'Delayed' && (
                <button onClick={() => handleUpdateStatus('Delayed')}>Set Delayed</button>
              )}
            </div>
          </div>
          <button className="driver-action-btn danger" onClick={handleDeactivate}>
            Deactivate
          </button>
        </div>
      </div>

      {/* Stats & Info Row */}
      <div className="driver-details-grid">
        {/* Performance Stats */}
        <div className="driver-stats-card">
          <h3 className="driver-section-title">Performance</h3>
          <div className="driver-stats-grid">
            <div className="driver-stat-item">
              <span className="driver-stat-value">{performance?.performance?.totalDeliveries || 0}</span>
              <span className="driver-stat-label">Total Deliveries</span>
            </div>
            <div className="driver-stat-item">
              <span className="driver-stat-value">{performance?.performance?.completedDeliveries || 0}</span>
              <span className="driver-stat-label">Completed</span>
            </div>
            <div className="driver-stat-item">
              <span className="driver-stat-value green">{performance?.performance?.onTimeRate || 100}%</span>
              <span className="driver-stat-label">On-Time Rate</span>
            </div>
            <div className="driver-stat-item">
              <span className="driver-stat-value">{performance?.todaysStats?.delivered || 0}/{performance?.todaysStats?.total || 0}</span>
              <span className="driver-stat-label">Today's Tasks</span>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="driver-info-card">
          <h3 className="driver-section-title">Contact Information</h3>
          <div className="driver-info-grid">
            <div className="driver-info-item">
              <label>Email</label>
              <span>{driver.email}</span>
            </div>
            <div className="driver-info-item">
              <label>Phone</label>
              <span>{driver.phone || 'Not provided'}</span>
            </div>
            <div className="driver-info-item">
              <label>Vehicle</label>
              <span>{driver.driverProfile?.vehicle || 'Not assigned'}</span>
            </div>
            <div className="driver-info-item">
              <label>License Number</label>
              <span>{driver.driverProfile?.licenseNumber || 'Not provided'}</span>
            </div>
            <div className="driver-info-item">
              <label>Joined</label>
              <span>{formatDate(driver.createdAt)}</span>
            </div>
            <div className="driver-info-item">
              <label>Current Location</label>
              {driver.driverProfile?.currentLocation?.latitude && driver.driverProfile?.currentLocation?.longitude ? (
                <button
                  className="driver-location-btn"
                  onClick={() => setShowLocationModal(true)}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1C4.5 1 2.5 3 2.5 5.5C2.5 9 7 13 7 13S11.5 9 11.5 5.5C11.5 3 9.5 1 7 1Z" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="7" cy="5.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  View on Map
                </button>
              ) : (
                <span className="driver-no-location">Location not available</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active Jobs Section */}
      {performance?.activeJobs?.length > 0 && (
        <div className="driver-active-jobs-card">
          <h3 className="driver-section-title">Active Jobs ({performance.activeJobs.length})</h3>
          <div className="driver-active-jobs-list">
            {performance.activeJobs.map((job) => (
              <div
                key={job._id}
                className="driver-active-job-item clickable"
                onClick={() => navigate(`/jobs/${job._id}`)}
              >
                <div className="driver-active-job-header">
                  <span className="driver-active-job-id">{job.jobId}</span>
                  <span className={`driver-job-status-badge ${getJobStatusClass(job.status)}`}>
                    {job.status}
                  </span>
                </div>
                <div className="driver-active-job-route">
                  <div className="driver-route-point pickup">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>{job.from?.name || job.from?.address?.substring(0, 30) || 'Pickup'}</span>
                  </div>
                  <div className="driver-route-line"></div>
                  <div className="driver-route-point delivery">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <circle cx="6" cy="6" r="4" fill="currentColor"/>
                    </svg>
                    <span>{job.to?.name || job.to?.address?.substring(0, 30) || 'Delivery'}</span>
                  </div>
                </div>
                <div className="driver-active-job-footer">
                  <span className="driver-view-details">View Details →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Jobs Table */}
      <div className="driver-jobs-card">
        <div className="driver-jobs-header">
          <h3 className="driver-section-title">Job History</h3>
          <div className="driver-jobs-filters">
            <div className="driver-jobs-search">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="6.5" cy="6.5" r="5" stroke="#9ca3af" strokeWidth="1.5"/>
                <path d="M10 10L14 14" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search jobs..."
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
              />
              {jobSearch && (
                <button className="driver-search-clear" onClick={() => setJobSearch('')}>×</button>
              )}
            </div>
            <select
              className="driver-jobs-filter"
              value={jobStatusFilter}
              onChange={(e) => setJobStatusFilter(e.target.value)}
            >
              {JOB_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {jobsLoading ? (
          <div className="driver-jobs-loading">Loading jobs...</div>
        ) : jobs.length === 0 ? (
          <div className="driver-jobs-empty">
            <p>No jobs found</p>
            <small>This driver has no job history matching the current filters</small>
          </div>
        ) : (
          <>
            <table className="driver-jobs-table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job._id}
                    className="driver-job-row clickable"
                    onClick={() => navigate(`/jobs/${job._id}`)}
                  >
                    <td className="driver-job-id-cell">
                      <span className="driver-job-id">{job.jobId}</span>
                    </td>
                    <td className="driver-job-location">
                      <span className="driver-location-name">{job.from?.name || 'N/A'}</span>
                      <span className="driver-location-address">{job.from?.address?.substring(0, 40) || ''}</span>
                    </td>
                    <td className="driver-job-location">
                      <span className="driver-location-name">{job.to?.name || 'N/A'}</span>
                      <span className="driver-location-address">{job.to?.address?.substring(0, 40) || ''}</span>
                    </td>
                    <td>
                      <span className={`driver-job-status-badge ${getJobStatusClass(job.status)}`}>
                        {job.status}
                      </span>
                    </td>
                    <td className="driver-job-date">
                      <span className="driver-date-main">{formatDate(job.createdAt)}</span>
                      <span className="driver-date-time">{formatTime(job.createdAt)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="driver-jobs-footer">
              <span className="driver-jobs-count">
                Showing {jobs.length} of {jobsPagination.total} jobs
              </span>
              <div className="driver-jobs-pagination">
                <button
                  className="driver-page-btn"
                  onClick={() => handleJobsPageChange(jobsPagination.page - 1)}
                  disabled={jobsPagination.page <= 1}
                >
                  Prev
                </button>
                {[...Array(Math.min(5, jobsPagination.totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      className={`driver-page-btn ${jobsPagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => handleJobsPageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  className="driver-page-btn"
                  onClick={() => handleJobsPageChange(jobsPagination.page + 1)}
                  disabled={jobsPagination.page >= jobsPagination.totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="driver-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="driver-modal" onClick={(e) => e.stopPropagation()}>
            <div className="driver-modal-header">
              <h3>Edit Driver</h3>
              <button className="driver-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="driver-modal-body">
              <div className="driver-form">
                <div className="driver-form-row">
                  <div className="driver-form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    />
                  </div>
                  <div className="driver-form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    />
                  </div>
                </div>
                <div className="driver-form-group">
                  <label>Email</label>
                  <input type="email" value={editForm.email} disabled />
                </div>
                <div className="driver-form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div className="driver-form-row">
                  <div className="driver-form-group">
                    <label>Vehicle</label>
                    <input
                      type="text"
                      value={editForm.vehicle}
                      onChange={(e) => setEditForm({ ...editForm, vehicle: e.target.value })}
                      placeholder="e.g., Van #22"
                    />
                  </div>
                  <div className="driver-form-group">
                    <label>License Number</label>
                    <input
                      type="text"
                      value={editForm.licenseNumber}
                      onChange={(e) => setEditForm({ ...editForm, licenseNumber: e.target.value })}
                      placeholder="DL-XXXXXXXX"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="driver-modal-footer">
              <button
                className="driver-btn-primary"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="driver-btn-outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && driver.driverProfile?.currentLocation && (
        <div className="driver-modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="driver-modal driver-location-modal" onClick={(e) => e.stopPropagation()}>
            <div className="driver-modal-header">
              <h3>Driver Location</h3>
              <button className="driver-modal-close" onClick={() => setShowLocationModal(false)}>×</button>
            </div>
            <div className="driver-modal-body">
              <div className="driver-location-info">
                <div className="driver-location-coords">
                  <div className="driver-coord-item">
                    <label>Latitude</label>
                    <span>{driver.driverProfile.currentLocation.latitude?.toFixed(6)}</span>
                  </div>
                  <div className="driver-coord-item">
                    <label>Longitude</label>
                    <span>{driver.driverProfile.currentLocation.longitude?.toFixed(6)}</span>
                  </div>
                  {driver.driverProfile.currentLocation.timestamp && (
                    <div className="driver-coord-item">
                      <label>Last Updated</label>
                      <span>{formatDate(driver.driverProfile.currentLocation.timestamp)} {formatTime(driver.driverProfile.currentLocation.timestamp)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="driver-map-container">
                <iframe
                  title="Driver Location Map"
                  width="100%"
                  height="350"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: '8px' }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${driver.driverProfile.currentLocation.longitude - 0.01},${driver.driverProfile.currentLocation.latitude - 0.01},${driver.driverProfile.currentLocation.longitude + 0.01},${driver.driverProfile.currentLocation.latitude + 0.01}&layer=mapnik&marker=${driver.driverProfile.currentLocation.latitude},${driver.driverProfile.currentLocation.longitude}`}
                  allowFullScreen
                />
              </div>
              <div className="driver-map-actions">
                <a
                  href={`https://www.google.com/maps?q=${driver.driverProfile.currentLocation.latitude},${driver.driverProfile.currentLocation.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="driver-btn-outline"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M10.5 3.5L3.5 10.5M10.5 3.5H5.5M10.5 3.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Open in Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
